"use client";

import { useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
} from "@tanstack/react-table";
import ShikiHighlighter from "react-shiki";
import {
  DownloadIcon,
  CopyIcon,
  CheckIcon,
  FileTextIcon,
  FileCodeIcon,
  TableIcon,
  PencilIcon,
  EyeIcon,
  SaveIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

// =============================================================================
// TYPES
// =============================================================================

export interface FileOutput {
  type: "file";
  file_id?: string;
  filename: string;
  mime_type?: string;
  url?: string; // URL to download/fetch content
  content?: string; // Inline content (for small files)
  size?: number;
}

type FileType = "code" | "csv" | "markdown" | "text";

// =============================================================================
// UTILS
// =============================================================================

const LANGUAGE_MAP: Record<string, string> = {
  // Python
  ".py": "python",
  ".pyw": "python",
  // JavaScript/TypeScript
  ".js": "javascript",
  ".jsx": "jsx",
  ".ts": "typescript",
  ".tsx": "tsx",
  // Data formats
  ".json": "json",
  ".yaml": "yaml",
  ".yml": "yaml",
  ".toml": "toml",
  // Markup
  ".md": "markdown",
  ".mdx": "mdx",
  ".html": "html",
  ".xml": "xml",
  // Shell
  ".sh": "bash",
  ".bash": "bash",
  ".zsh": "zsh",
  // Config
  ".env": "shell",
  ".ini": "ini",
  ".conf": "ini",
  // SQL
  ".sql": "sql",
  // Go
  ".go": "go",
  // Rust
  ".rs": "rust",
  // CSS
  ".css": "css",
  ".scss": "scss",
  // Other
  ".dockerfile": "dockerfile",
  ".graphql": "graphql",
};

function getFileExtension(filename: string): string {
  const match = filename.match(/\.[^.]+$/);
  return match ? match[0].toLowerCase() : "";
}

function getLanguage(filename: string): string {
  const ext = getFileExtension(filename);
  return LANGUAGE_MAP[ext] || "text";
}

function getFileType(filename: string, mimeType?: string): FileType {
  const ext = getFileExtension(filename);

  if (ext === ".csv" || mimeType === "text/csv") return "csv";
  if (ext === ".md" || ext === ".mdx") return "markdown";
  if (LANGUAGE_MAP[ext]) return "code";
  return "text";
}

function parseCSV(content: string): { headers: string[]; rows: string[][] } {
  const lines = content.trim().split("\n");
  if (lines.length === 0) return { headers: [], rows: [] };

  const parseRow = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === "," && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const headers = parseRow(lines[0]);
  const rows = lines.slice(1).map(parseRow);

  return { headers, rows };
}

function getFileIcon(fileType: FileType) {
  switch (fileType) {
    case "csv":
      return <TableIcon className="h-4 w-4" />;
    case "code":
      return <FileCodeIcon className="h-4 w-4" />;
    default:
      return <FileTextIcon className="h-4 w-4" />;
  }
}

// =============================================================================
// FILE LINK (Clickable link that opens viewer)
// =============================================================================

interface FileLinkProps {
  file: FileOutput;
  className?: string;
  editable?: boolean;
  onSave?: (file: FileOutput, newContent: string) => void | Promise<void>;
}

export function FileLink({ file, className, editable = false, onSave }: FileLinkProps) {
  const [isOpen, setIsOpen] = useState(false);
  const fileType = getFileType(file.filename, file.mime_type);

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          "inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs",
          "bg-muted hover:bg-secondary text-blue-400 hover:text-blue-300",
          "border border-border transition-colors",
          className
        )}
      >
        {getFileIcon(fileType)}
        <span className="font-mono">{file.filename}</span>
        {file.size && (
          <span className="text-muted-foreground text-[10px]">
            ({formatFileSize(file.size)})
          </span>
        )}
        {editable && (
          <PencilIcon className="h-3 w-3 text-muted-foreground" />
        )}
      </button>

      <FileViewerDialog
        file={file}
        open={isOpen}
        onOpenChange={setIsOpen}
        editable={editable}
        onSave={onSave}
      />
    </>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// =============================================================================
// FILE VIEWER DIALOG
// =============================================================================

interface FileViewerDialogProps {
  file: FileOutput;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editable?: boolean;
  onSave?: (file: FileOutput, newContent: string) => void | Promise<void>;
}

export function FileViewerDialog({
  file,
  open,
  onOpenChange,
  editable = false,
  onSave,
}: FileViewerDialogProps) {
  const [copied, setCopied] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editContent, setEditContent] = useState(file.content || "");
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const fileType = getFileType(file.filename, file.mime_type);
  const language = getLanguage(file.filename);

  // Reset edit state when dialog opens/closes
  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen) {
      setIsEditMode(false);
      setEditContent(file.content || "");
      setHasChanges(false);
    }
    onOpenChange(newOpen);
  }, [file.content, onOpenChange]);

  const handleCopy = useCallback(async () => {
    const contentToCopy = isEditMode ? editContent : file.content;
    if (!contentToCopy) return;
    await navigator.clipboard.writeText(contentToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [file.content, editContent, isEditMode]);

  const handleDownload = useCallback(() => {
    const contentToDownload = isEditMode ? editContent : file.content;
    if (file.url && !isEditMode) {
      window.open(file.url, "_blank");
      return;
    }
    if (contentToDownload) {
      const blob = new Blob([contentToDownload], { type: file.mime_type || "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }, [file, editContent, isEditMode]);

  const handleEditToggle = useCallback(() => {
    if (isEditMode && hasChanges) {
      // Switching from edit to view - confirm discard
      if (!confirm("Discard unsaved changes?")) return;
      setEditContent(file.content || "");
      setHasChanges(false);
    }
    setIsEditMode(!isEditMode);
  }, [isEditMode, hasChanges, file.content]);

  const handleContentChange = useCallback((newContent: string) => {
    setEditContent(newContent);
    setHasChanges(newContent !== file.content);
  }, [file.content]);

  const handleSave = useCallback(async () => {
    if (!onSave || !hasChanges) return;
    setIsSaving(true);
    try {
      await onSave(file, editContent);
      setHasChanges(false);
      // Optionally switch back to view mode after save
      // setIsEditMode(false);
    } catch (err) {
      console.error("Failed to save file:", err);
      alert("Failed to save file");
    } finally {
      setIsSaving(false);
    }
  }, [file, editContent, hasChanges, onSave]);

  // CSV is not editable in table mode
  const canEdit = editable && fileType !== "csv";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col bg-card border-border">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 text-foreground">
              {getFileIcon(fileType)}
              <span className="font-mono">{file.filename}</span>
              <span className="text-xs text-muted-foreground font-normal">
                {language}
              </span>
              {isEditMode && hasChanges && (
                <span className="text-xs text-amber-400 font-normal">
                  (modified)
                </span>
              )}
            </DialogTitle>
            <div className="flex items-center gap-1">
              {/* Edit/View Toggle */}
              {canEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEditToggle}
                  className={cn(
                    "text-muted-foreground hover:text-foreground",
                    isEditMode && "bg-muted text-blue-400"
                  )}
                  title={isEditMode ? "View mode" : "Edit mode"}
                >
                  {isEditMode ? (
                    <EyeIcon className="h-4 w-4" />
                  ) : (
                    <PencilIcon className="h-4 w-4" />
                  )}
                </Button>
              )}

              {/* Save Button */}
              {isEditMode && onSave && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSave}
                  disabled={!hasChanges || isSaving}
                  className={cn(
                    "text-muted-foreground hover:text-foreground",
                    hasChanges && "text-emerald-400 hover:text-emerald-300"
                  )}
                  title="Save changes"
                >
                  <SaveIcon className="h-4 w-4" />
                </Button>
              )}

              {/* Copy Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopy}
                disabled={!file.content && !editContent}
                className="text-muted-foreground hover:text-foreground"
                title="Copy to clipboard"
              >
                {copied ? (
                  <CheckIcon className="h-4 w-4" />
                ) : (
                  <CopyIcon className="h-4 w-4" />
                )}
              </Button>

              {/* Download Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDownload}
                className="text-muted-foreground hover:text-foreground"
                title="Download file"
              >
                <DownloadIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto min-h-0">
          {!file.content && !isEditMode ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              No content available
            </div>
          ) : isEditMode ? (
            <CodeEditor
              content={editContent}
              onChange={handleContentChange}
              language={language}
            />
          ) : fileType === "csv" ? (
            <CSVViewer content={file.content!} />
          ) : (
            <CodeViewer content={file.content!} language={language} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// CODE VIEWER (Syntax Highlighting)
// =============================================================================

export interface CodeViewerProps {
  content: string;
  language: string;
  className?: string;
}

export function CodeViewer({ content, language, className }: CodeViewerProps) {
  return (
    <div className={cn("rounded-lg overflow-hidden bg-background", className)}>
      <ShikiHighlighter
        language={language}
        theme="github-dark"
      >
        {content}
      </ShikiHighlighter>
    </div>
  );
}

// =============================================================================
// CODE EDITOR (Textarea with monospace font)
// =============================================================================

interface CodeEditorProps {
  content: string;
  onChange: (content: string) => void;
  language: string;
}

function CodeEditor({ content, onChange, language }: CodeEditorProps) {
  return (
    <div className="rounded-lg overflow-hidden bg-background h-full">
      <div className="flex items-center justify-between px-3 py-1.5 bg-muted border-b border-border">
        <span className="text-xs text-muted-foreground">Editing: {language}</span>
        <span className="text-xs text-muted-foreground">
          {content.split("\n").length} lines
        </span>
      </div>
      <textarea
        value={content}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "w-full h-[calc(100%-32px)] min-h-[300px] p-4",
          "bg-background text-foreground font-mono text-sm",
          "border-none outline-none resize-none",
          "focus:ring-0 focus:outline-none"
        )}
        spellCheck={false}
        autoCapitalize="off"
        autoComplete="off"
        autoCorrect="off"
      />
    </div>
  );
}

// =============================================================================
// CSV VIEWER (Table)
// =============================================================================

export interface CSVViewerProps {
  content: string;
  className?: string;
}

export function CSVViewer({ content, className }: CSVViewerProps) {
  const { headers, rows } = useMemo(() => parseCSV(content), [content]);

  const columns: ColumnDef<string[]>[] = useMemo(
    () =>
      headers.map((header, index) => ({
        id: `col-${index}`,
        header: () => <span className="font-semibold">{header}</span>,
        cell: ({ row }) => row.original[index] || "",
      })),
    [headers]
  );

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (headers.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground">
        Empty CSV file
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <Table>
        <TableHeader className="bg-muted">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} className="text-foreground">
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className="text-foreground font-mono text-xs">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="px-3 py-2 text-xs text-muted-foreground bg-muted/50 border-t border-border">
        {rows.length} rows, {headers.length} columns
      </div>
    </div>
  );
}

// =============================================================================
// HELPERS FOR DETECTING FILES IN TOOL OUTPUTS
// =============================================================================

/**
 * Check if a value looks like a file output
 */
export function isFileOutput(value: unknown): value is FileOutput {
  if (!value || typeof value !== "object") return false;
  const obj = value as Record<string, unknown>;
  return obj.type === "file" && typeof obj.filename === "string";
}

/**
 * Extract file outputs from tool result (can be nested)
 */
export function extractFileOutputs(result: unknown): FileOutput[] {
  const files: FileOutput[] = [];

  function traverse(value: unknown) {
    if (isFileOutput(value)) {
      files.push(value);
    } else if (Array.isArray(value)) {
      value.forEach(traverse);
    } else if (value && typeof value === "object") {
      Object.values(value as Record<string, unknown>).forEach(traverse);
    }
  }

  traverse(result);
  return files;
}
